import {
  buildSearchContextSummary,
  toPreferenceLabel,
} from "./searchContextSummary";

/**
 * Build preference tags from the same fields as the "Based on:" summary.
 */
function constraintChipLabel(searchParams) {
  const raw = searchParams?.productConstraint;
  if (!raw) return "";
  if (typeof raw === "string") return raw.trim();
  return String(raw.label || "").trim();
}

function budgetChipLabel(searchParams) {
  if (searchParams?.budget?.max == null) return "";
  const max = Number(searchParams.budget.max);
  if (!Number.isFinite(max)) return "";
  return `up to $${max.toLocaleString()}`;
}

export function buildMatchTagLabels(searchParams) {
  if (!searchParams) return [];

  const labels = [];
  const constraint = constraintChipLabel(searchParams);
  if (constraint) labels.push(constraint);

  const useCase = toPreferenceLabel(searchParams.useCase);
  if (useCase && !labels.includes(useCase)) {
    labels.push(useCase);
  }

  (searchParams.mustHaves || []).forEach((item) => {
    const label = toPreferenceLabel(item);
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  });

  const budgetLabel = budgetChipLabel(searchParams);
  if (budgetLabel && !labels.includes(budgetLabel)) {
    labels.push(budgetLabel);
  }

  if (!constraint) return labels.slice(0, 3);

  const middle = labels.filter(
    (label) => label !== constraint && label !== budgetLabel,
  );
  const next = [constraint, ...middle.slice(0, 2)];
  if (budgetLabel) next.push(budgetLabel);
  return next;
}

const formatBareAmount = (amount) => {
  const value = Number(String(amount).replace(/,/g, ""));
  if (!Number.isFinite(value)) return amount;
  return `$${value.toLocaleString()}`;
};

const resolveBrandLabel = (product) => {
  if (!product?.brand) return null;
  return typeof product.brand === "object" ? product.brand?.name : product.brand;
};

/**
 * Drop AI blurbs that describe a different shortlisted product
 * (e.g. Lenovo title + ASUS explanation).
 */
function productHaystack(product) {
  const brand =
    typeof product?.brand === "object" ? product.brand?.name : product?.brand;
  return `${brand || ""} ${product?.name || ""} ${product?.model || ""}`;
}

function productMatchesLine(product, constraint) {
  if (!constraint?.label && !constraint?.phrase && !constraint?.pattern) {
    return true;
  }
  const source = constraint.pattern
    ? constraint.pattern
    : String(constraint.phrase || constraint.label || "")
        .trim()
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        .replace(/\s+/g, "\\s+");
  if (!source) return true;
  try {
    return new RegExp(source, "i").test(productHaystack(product));
  } catch {
    return true;
  }
}

export function sanitizeMatchExplanationForProduct(
  explanation,
  product,
  siblings = [],
  searchParams = null,
) {
  if (typeof explanation !== "string" || !explanation.trim()) return null;
  const text = explanation.toLowerCase();
  const ownId = String(product?._id || "");
  const ownName = String(product?.name || "").toLowerCase();
  const ownBrand = String(resolveBrandLabel(product) || "").toLowerCase();

  for (const sibling of siblings) {
    if (String(sibling?._id) === ownId) continue;
    const otherName = String(sibling?.name || "")
      .trim()
      .toLowerCase();
    if (otherName.length >= 6 && text.includes(otherName)) {
      if (!ownName.includes(otherName)) return null;
    }
    const otherBrand = String(resolveBrandLabel(sibling) || "")
      .trim()
      .toLowerCase();
    if (
      otherBrand.length >= 3 &&
      text.includes(otherBrand) &&
      otherBrand !== ownBrand &&
      !ownName.includes(otherBrand)
    ) {
      return null;
    }
  }

  const constraint = searchParams?.productConstraint;
  const lineLabel = constraintChipLabel(searchParams);
  if (
    lineLabel &&
    text.includes(lineLabel.toLowerCase()) &&
    !productMatchesLine(product, constraint)
  ) {
    return null;
  }

  return explanation;
}

export function fallbackMatchExplanation(product, searchParams) {
  const name = product?.name;
  if (!name) return null;
  const constraint = searchParams?.productConstraint;
  if (constraint?.unmatched && constraint.label) {
    const max = Number(searchParams?.budget?.max);
    const budget =
      !searchParams?.budgetAny && Number.isFinite(max) && max > 0
        ? ` Your budget is up to $${max.toLocaleString()}.`
        : "";
    return `${name} is a closest alternative, not a ${constraint.label}.${budget}`;
  }
  const price =
    product?.price != null
      ? ` at $${Number(product.price).toLocaleString()}`
      : "";
  return `${name} is a strong fit for your request${price}.`;
}

/**
 * Normalize AI match explanations so currency references use $ and commas.
 */
export function normalizeMatchExplanation(text, searchParams = null) {
  if (typeof text !== "string" || !text.trim()) return text;

  let normalized = text;

  normalized = normalized.replace(
    /\b(?:within your|under the|under your|below your|within)\s+(\d{1,3}(?:,\d{3})*|\d+)\s+budget\b/gi,
    (_, amount) => `within your ${formatBareAmount(amount)} budget`,
  );

  normalized = normalized.replace(
    /\bstaying under the\s+(\d{1,3}(?:,\d{3})*|\d+)\s+limit\b/gi,
    (_, amount) => `staying under the ${formatBareAmount(amount)} limit`,
  );

  normalized = normalized.replace(
    /\bat a price of\s+(\d{1,3}(?:,\d{3})*|\d+)\b/gi,
    (_, amount) => `at a price of ${formatBareAmount(amount)}`,
  );

  if (searchParams?.budget?.max != null && !searchParams?.budgetAny) {
    const max = Number(searchParams.budget.max);
    if (Number.isFinite(max) && max > 0) {
      const formatted = `$${max.toLocaleString()}`;
      const denials = [
        [/\bno budget limit\b/gi, `a budget up to ${formatted}`],
        [/\bunlimited budget\b/gi, `a budget up to ${formatted}`],
        [/\bwithout a budget\b/gi, `within ${formatted}`],
        [/\bprice (?:does not|doesn't) matter\b/gi, `your budget is ${formatted}`],
        [/\bno price limit\b/gi, `a budget up to ${formatted}`],
        [/\bno budget\b/gi, `a budget up to ${formatted}`],
      ];
      denials.forEach(([pattern, replacement]) => {
        normalized = normalized.replace(pattern, replacement);
      });
      // Skip amounts already prefixed with $ (e.g. "under $600" → avoid "$$600").
      normalized = normalized.replace(
        new RegExp(
          `(?<!\\$)\\b${max}\\b(?!\\s*(?:GB|MB|mAh|Hz|inch|mm|W))`,
          "g",
        ),
        formatted,
      );
    }
  }

  // Collapse any double-dollar artifacts from prior normalization passes.
  normalized = normalized.replace(/\$\$(?=\d)/g, "$");

  return normalized;
}

export function buildMatchContextSummary(searchParams) {
  return buildSearchContextSummary(searchParams);
}
